import {
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import {
  Button,
  Colors,
  IconButton,
  TextBold,
  TextReg,
  isAndroid,
  useDynamicStyles,
  useMediaQueries,
} from '@sunrise-ui/primitives';
import { isPastDay } from '@utils/timing';
import { DateTime } from 'luxon';
import React, { useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import type { MarkedDates } from 'react-native-calendars/src/types';
import { CalendarModal, GET_CALENDAR_MODAL_WIDTH } from './CalendarModal';

// Type definitions
type NavigationDirection = 'prev' | 'next';
type WeekDayName = 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat';
type CalendarDayPress = { dateString: string };

type Props = {
  /** ISO date string in YYYY-MM-DD format */
  selectedDate: DateTime<true>;
  /** Callback when date is changed, receives ISO date string */
  onDateChanged: (date: string) => void;
  /** Marked dates for events display */
  markedDates?: MarkedDates;
  /** Reduce top/bottom spacing for embedded calendar use */
  compact?: boolean;
  /** Allow view and selection of dates in the past */
  allowPastSelection?: boolean;
};

export const CalendarCustomWeek = ({
  selectedDate,
  onDateChanged,
  markedDates = {},
  compact = false,
  allowPastSelection = false,
}: Props) => {
  const styles = useDynamicStyles(createStyles);
  const { isMobile } = useMediaQueries();
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const buttonRef = useRef<View>(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const isoSelectedDate = selectedDate.toISODate();
  const monthYear = selectedDate.toFormat('MMMM yyyy');

  // Get the week days starting from Sunday
  const weekDays = useMemo((): DateTime<true>[] => {
    // Get the start of the week (Sunday) by calculating based on weekday
    // Luxon weekday: Monday=1, Tuesday=2, ..., Sunday=7
    const weekday = selectedDate.weekday;
    const daysFromSunday = weekday === 7 ? 0 : weekday; // Sunday=7 becomes 0, others stay the same
    const startOfWeek = selectedDate.minus({ days: daysFromSunday });
    const days: DateTime[] = [];

    for (let i = 0; i < 7; i++) {
      const day = startOfWeek.plus({ days: i });
      days.push(day);
    }

    return days;
  }, [selectedDate]);

  const openCalendar = () => {
    if (buttonRef.current) {
      buttonRef.current.measureInWindow((x, y, width, height) => {
        setMenuPosition({
          top: y + height + 12 + (isAndroid ? 20 : 0),
          left: x + width / 2 - GET_CALENDAR_MODAL_WIDTH(isMobile) / 2,
        });
        setIsCalendarVisible(true);
      });
    }
  };

  const closeCalendar = () => {
    setIsCalendarVisible(false);
  };

  const handleCalendarDayPress = (day: CalendarDayPress): void => {
    onDateChanged(day.dateString);
    closeCalendar();
  };

  const finalMarkedDates = useMemo(
    () => ({
      ...markedDates,
      [isoSelectedDate]: {
        ...markedDates[isoSelectedDate],
        selected: true,
        selectedColor: Colors.purple1,
      },
    }),
    [markedDates, selectedDate],
  );

  const navigateWeek = (direction: NavigationDirection): void => {
    const newDate =
      direction === 'prev'
        ? selectedDate.minus({ weeks: 1 })
        : selectedDate.plus({ weeks: 1 });

    // If navigating back and the selected day would be in the past, select today instead
    if (direction === 'prev' && isPastDay(newDate) && !allowPastSelection) {
      const today = DateTime.now();
      onDateChanged(today.toISODate());
    } else if (newDate.isValid) {
      onDateChanged(newDate.toISODate());
    }
  };

  const handleDayPress = (day: DateTime<true>): void => {
    // Don't allow selection of past days
    if (!allowPastSelection && isPastDay(day)) return;
    onDateChanged(day.toISODate());
  };

  // Check if the last week is in the past
  const isLastWeekInThePast = useMemo((): boolean => {
    const endOfLastWeek = weekDays[0].minus({ days: 1 });
    return endOfLastWeek < DateTime.now().startOf('day');
  }, [weekDays]);

  const getDayTextStyle = (day: DateTime) => {
    if (!allowPastSelection && isPastDay(day)) {
      return [styles.dayText, styles.disabledDayText];
    }
    if (day.hasSame(selectedDate, 'day')) {
      return [styles.dayText, styles.selectedDayText];
    }
    if (day.hasSame(DateTime.now(), 'day')) {
      return [styles.dayText, styles.todayText];
    }
    return styles.dayText;
  };

  return (
    <View style={[styles.container, compact && styles.compactContainer]}>
      {/* Header with month/year and calendar button */}
      <View style={[styles.header, compact && styles.compactHeader]}>
        <View style={styles.buttonContainer}>
          <Button
            style={styles.button}
            labelStyle={styles.buttonLabel}
            onPress={openCalendar}
            size="small"
            ref={buttonRef}
          >
            <TextBold>{monthYear}</TextBold>
          </Button>
          <View style={styles.buttonIcon}>
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              size={16}
              stroke={8}
              color={Colors.text}
            />
          </View>
        </View>
      </View>

      {/* Calendar content with navigation buttons */}
      <View style={styles.calendarContent}>
        <IconButton
          icon={ArrowLeft01Icon}
          onPress={() => navigateWeek('prev')}
          size="small"
          iconColor={Colors.purple1}
          style={[
            styles.navButton,
            isLastWeekInThePast && styles.disabledNavButton,
          ]}
          backgroundColor="transparent"
          disabled={allowPastSelection ? false : isLastWeekInThePast}
        />

        <View style={styles.calendarSection}>
          {/* Day names header */}
          <View style={styles.dayNamesContainer}>
            {(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const).map(
              (dayName: WeekDayName) => (
                <View key={dayName} style={styles.dayNameContainer}>
                  <TextReg style={styles.dayNameText}>{dayName}</TextReg>
                </View>
              ),
            )}
          </View>

          {/* Week days */}
          <View style={styles.weekContainer}>
            {weekDays.map((day) => (
              <Pressable
                key={day.toISODate()}
                style={[
                  styles.dayContainer,
                  !allowPastSelection &&
                    isPastDay(day) &&
                    styles.disabledDayContainer,
                ]}
                onPress={() => handleDayPress(day)}
                disabled={allowPastSelection ? false : isPastDay(day)}
              >
                <View
                  style={[
                    styles.dayCircle,
                    !allowPastSelection &&
                      isPastDay(day) &&
                      styles.disabledDayCircle,
                    day.hasSame(selectedDate, 'day') &&
                      styles.selectedDayCircle,
                  ]}
                >
                  <TextReg style={getDayTextStyle(day)}>{day.day}</TextReg>
                </View>
                <View style={styles.eventDotContainer}>
                  {markedDates[day.toISODate()]?.dots?.map((_, index) => (
                    <View key={index} style={styles.eventDot} />
                  ))}
                </View>
              </Pressable>
            ))}
          </View>
        </View>

        <IconButton
          icon={ArrowRight01Icon}
          onPress={() => navigateWeek('next')}
          size="small"
          iconColor={Colors.purple1}
          style={styles.navButton}
          backgroundColor="transparent"
        />
      </View>

      <CalendarModal
        isVisible={isCalendarVisible}
        onClose={closeCalendar}
        onDayPress={handleCalendarDayPress}
        initialDate={isoSelectedDate}
        minDate={allowPastSelection ? undefined : DateTime.now().toISODate()}
        markedDates={finalMarkedDates}
        position={menuPosition}
      />
    </View>
  );
};

const createStyles = () =>
  StyleSheet.create({
    buttonContainer: {
      position: 'relative',
      alignItems: 'center',
    },
    button: {
      backgroundColor: Colors.purple0,
    },
    buttonLabel: {
      marginRight: 0,
      marginLeft: 0,
      marginBottom: 0,
      marginTop: 0,
      paddingHorizontal: 12,
      paddingRight: 30,
      fontSize: 18,
      lineHeight: 30,
      color: Colors.text,
    },
    buttonIcon: {
      position: 'absolute',
      pointerEvents: 'none',
      right: 8,
      height: 30,
      justifyContent: 'center',
    },
    container: {
      backgroundColor: Colors.purple0,
      paddingVertical: 16,
    },
    compactContainer: {
      paddingVertical: 0,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 16,
      marginBottom: 16,
    },
    compactHeader: {
      marginBottom: 4,
    },
    calendarContent: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    navButton: {
      marginHorizontal: 8,
    },
    calendarSection: {
      flex: 1,
    },
    dayNamesContainer: {
      flexDirection: 'row',
      marginBottom: 8,
    },
    dayNameContainer: {
      flex: 1,
      alignItems: 'center',
    },
    dayNameText: {
      fontSize: 12,
    },
    weekContainer: {
      flexDirection: 'row',
    },
    dayContainer: {
      flex: 1,
      paddingTop: 4,
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    },
    dayCircle: {
      width: 28,
      height: 28,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 14,
    },
    selectedDayCircle: {
      backgroundColor: Colors.purple1,
    },
    dayText: {
      fontSize: 16,
      color: Colors.text,
    },
    selectedDayText: {
      color: Colors.text,
      fontWeight: '600',
    },
    todayText: {
      color: Colors.purple1,
      fontWeight: '600',
    },
    disabledDayText: {
      color: Colors.opacity20,
    },
    disabledDayCircle: {
      backgroundColor: 'transparent',
    },
    disabledDayContainer: {
      opacity: 0.9,
    },
    disabledNavButton: {
      opacity: 0.5,
    },
    eventDotContainer: {
      position: 'absolute',
      bottom: -8,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
    },
    eventDot: {
      width: 4,
      height: 4,
      borderRadius: 2,
      backgroundColor: Colors.pink0,
    },
  });
